import { getTenantId, setTenantId } from '@innovyze/lib_get_service';
import {
	setDefaultSite,
	setToken,
	setUser,
} from '../../../actions/authenticationContext.actions';
import { useProjects } from '../../../hooks/useProjects';
import {
	getAutodeskUserProfile,
	getUserInfoV2,
	getAutodeskContext,
	getAutodeskContextByOrgId,
} from '../../../services/forge.service';
import { AutodeskContext, AutodeskUserProfile } from '../../../types/forge';

import { useDispatch, useSelector } from 'react-redux';
import { setUserContext } from '../../../actions/userContext.actions';
import { SiteConfig } from '../../../contexts/SiteConfiguration';
import { selectApplicationName } from '../../../selectors';
import { processUserContextToStore } from '../../../utils/UserContext';
import { STYLOVYZE_FLAGS } from '../../../types/features';
import { useIsFeatureEnabled } from '../../../utils/features';

export const useForgeUserContext = (config: SiteConfig | null) => {
	const dispatch = useDispatch();
	const { activeProject } = useProjects();
	const appName = useSelector(selectApplicationName);
	const useV2Profile = !!useIsFeatureEnabled(STYLOVYZE_FLAGS.USER_PROFILE_V2);

	// Workaround for feature toggles not being loaded
	// TODO: Remove this once product picker toggle is removed
	const enteredFromProjectInjection = localStorage.getItem('projectFlow');

	const getUserProfileData = async (accessToken?: string) => {
		console.log('Fetching user profile - useV2Profile:', useV2Profile);

		if (useV2Profile) {
			try {
				return await getUserInfoV2(config?.forge, accessToken);
			} catch (error) {
				console.error(
					'Failed to fetch user profile from V2 API, falling back to V1:',
					error,
				);
				return await getAutodeskUserProfile(config?.forge, accessToken);
			}
		} else {
			return await getAutodeskUserProfile(config?.forge, accessToken);
		}
	};

	const retrieveUserContext = async (
		accessToken: string,
		isLandingPage?: boolean,
	) => {
		// TODO pipe this through redux instead
		// NEW TODO this needs to be handled via forking logic, not serially
		const userProfile = await getUserProfileData(accessToken);
		if (!isLandingPage) {
			let autodeskUserContext: AutodeskContext;
			if (enteredFromProjectInjection && !!activeProject) {
				autodeskUserContext = await getAutodeskContext(
					appName,
					activeProject?.hubUrn || null,
					activeProject?.id || null,
				);
			} else {
				autodeskUserContext = await getAutodeskContextByOrgId(
					appName,
					getTenantId() || null,
				);
			}
			return {
				autodeskUserContext,
				userProfile,
			};
		}

		return { userProfile };
	};

	const storeUserContext = (
		autodeskUserContext: AutodeskContext | null,
		userProfile: AutodeskUserProfile,
		accessToken: string,
		isLandingPage?: boolean,
	) => {
		const { defaultSite, userContext } = processUserContextToStore(
			{
				autodeskUserContext,
				userProfile,
				accessToken,
			},
			isLandingPage,
		);
		dispatch(setToken(accessToken));
		dispatch(setUser(userProfile));
		dispatch(setUserContext(userContext));
		dispatch(setDefaultSite(!isLandingPage ? defaultSite : 'LandingPage'));
		if (!isLandingPage) {
			setTenantId(defaultSite);
		}
	};

	return { retrieveUserContext, storeUserContext };
};
